import React from 'react';

/**
 * GOOGLE SIGN-IN BUTTON — the one sanctioned exception to our monochrome, Lucide-only design language.
 *
 * Google's Sign-In branding guidelines REQUIRE their unmodified four-colour "G" and one of their approved
 * wordings; recolouring the mark or substituting a generic icon is not permitted for a button that performs
 * Google sign-in. So this is the single place in the product where colour appears as branding rather than as
 * state — everywhere else the monochrome rule stands.
 *
 * The mark is inlined as SVG rather than fetched from Google's CDN: the artifact CSP blocks external hosts,
 * and a sign-in button that silently loses its logo is worse than one that never had it.
 *
 * Colours follow Google's light/dark specification (light: #FFFFFF on #747775 border, #1F1F1F text; dark:
 * #131314 on #8E918F border, #E3E3E3 text) and are therefore hardcoded, not themed through our tokens —
 * the whole point is that this button looks the same in every product that uses it.
 */

/** Google's "G" mark. Unmodified — the four brand colours are part of the trademark. */
const GoogleG: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden focusable="false">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export interface GoogleSignInButtonProps {
  onClick: () => void;
  disabled?: boolean;
  /** Google permits either wording. "Continue" suits a flow already in progress (an invite, a checkout). */
  label?: 'Sign in with Google' | 'Continue with Google';
  /** Stretch to the container — used where the button is the page's single action. */
  fullWidth?: boolean;
  className?: string;
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onClick, disabled, label = 'Sign in with Google', fullWidth, className = '',
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid="google-signin"
    // 44px, not Google's 40px minimum: our own touch-target rule is the stricter of the two, and this is the
    // most-repeated control on the site.
    className={`inline-flex min-h-[44px] items-center justify-center gap-3 rounded-xl border
                border-[#747775] bg-white px-6 text-sm font-medium text-[#1F1F1F]
                transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50
                dark:border-[#8E918F] dark:bg-[#131314] dark:text-[#E3E3E3]
                ${fullWidth ? 'w-full' : ''} ${className}`}
  >
    <GoogleG className="h-5 w-5 shrink-0" />
    {label}
  </button>
);

export default GoogleSignInButton;
